import { FC, MouseEventHandler, memo, useState } from 'react';
import { MdOpenInFull } from 'react-icons/md';
import ImageLightbox from '@/components/ui/ImageLightbox';
import { Carousel } from "react-responsive-carousel";
import CarouselArrow from '@/components/ui/CarouselArrow';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/Store';
import { Card } from '@mui/material';
import { useImageCarouselStyles } from './ImageCarousel.styles';
import { Skeleton } from '../../dumb/Skeleton';

require("@/assets/css/carousel.css");

interface ImageCarouselProps {
    images: Array<string>;
    showLoading?: boolean;
}

const ImageCarousel: FC<ImageCarouselProps> = (props) => {
    const { images, showLoading = false } = props;

    const { settings } = useSelector((state: RootState) => state.settings);

    const { classes: { image, arrows, leftArrow, rightArrow, thumbnail, imageSlide } } = useImageCarouselStyles();
    const [current, setCurrent] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const canEnlarge = !showLoading && images?.length > 0;

    const renderArrowPrev = (onClickHandler: MouseEventHandler<HTMLButtonElement>, hasPrev: boolean, label: string) =>
        hasPrev && <CarouselArrow direction="left" onClick={onClickHandler} label={label} />;

    const renderArrowNext = (onClickHandler: MouseEventHandler<HTMLButtonElement>, hasNext: boolean, label: string) =>
        hasNext && <CarouselArrow direction="right" onClick={onClickHandler} label={label} />;

    const customRenderThumb = (children) =>
        children.map((item, idx) => (
            showLoading ? (
                <Skeleton key={idx} animation="wave" variant="rectangular" height={70} width={70} />
            ) : (
                <img
                    key={item?.key}
                    className={thumbnail}
                    src={item?.props?.url}
                    alt="thumbnail"
                />
            )
        ));

    const ImageSlide = ({ url }: { url: string }) => (
        <div className="carousel-wrapper">
            <img
                src={url}
                className={imageSlide}
                alt="carousel slide"
            />
        </div>
    );

    if (showLoading) {
        return (
            <div className="pb-carousel-skeleton">
                <Skeleton animation="wave" variant="rectangular" className="pb-carousel-skeleton-main" />
                <div className="pb-carousel-skeleton-thumbs">
                    {Array.from({ length: 4 }).map((_, idx) => <Skeleton key={idx} animation="wave" variant="rectangular" width={70} height={70} style={{ borderRadius: 10 }} />)}
                </div>
            </div>
        );
    }

    return (
        <Card className={image + " pb-carousel"} style={{ position: "relative" }}>
            {canEnlarge && (
                <button type="button" className="pb-enlarge" aria-label="View large image" onClick={() => setLightboxOpen(true)}>
                    <MdOpenInFull size={18} />
                </button>
            )}
            <Carousel
                selectedItem={current}
                onChange={(index) => setCurrent(index)}
                onClickItem={(index) => { if (canEnlarge) { setCurrent(index); setLightboxOpen(true); } }}
                thumbWidth={70}
                stopOnHover={true}
                infiniteLoop={true}
                autoPlay={false}
                emulateTouch={true}
                showStatus={false}
                useKeyboardArrows={true}
                showThumbs={true}
                dynamicHeight={false}
                showArrows={true}
                renderArrowPrev={renderArrowPrev}
                renderArrowNext={renderArrowNext}
                renderThumbs={customRenderThumb}
            >
                {
                    showLoading ? (
                        Array.from({ length: 10 }).map((_, idx) => (
                            <ImageSlide key={idx} url={"https://via.placeholder.com/1928x1240?text=Loading"} />
                        ))
                    ) : (
                        images.map((imageUrl, idx) => (
                            <ImageSlide key={idx} url={imageUrl} />
                        ))
                    )
                }
            </Carousel>
            {canEnlarge && (
                <ImageLightbox images={images} index={Math.min(current, images.length - 1)} open={lightboxOpen} onClose={() => setLightboxOpen(false)} onIndexChange={setCurrent} />
            )}
        </Card>
    );
};

export default memo(ImageCarousel);
